import { useNavigate } from '@tanstack/react-router'
import { useMemo, useState, type SyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { ErrorState } from '@/components/feedback/error-state'
import { InlineNotice } from '@/components/feedback/inline-notice'
import { Skeleton } from '@/components/feedback/skeleton'
import { FormField } from '@/components/forms/form-field'
import { Input } from '@/components/forms/input'
import { PasswordInput } from '@/components/forms/password-input'
import { PageHeader } from '@/components/layout/page-header'
import { Modal } from '@/components/overlays/modal'
import { Button } from '@/components/primitives/button'
import { IconButton } from '@/components/primitives/icon-button'
import {
  getDeviceTimezone,
  getSupportedTimezones,
  getTimezoneDisplayOptions,
} from '@/features/shared/timezone/timezone'
import { UserShellPage } from '@/features/user/shell/shell-page'
import { cn } from '@/lib/cn'
import { useMediaQuery } from '@/lib/use-media-query'

import type { UserProfile } from './api'
import { ProfileMobilePage } from './mobile/profile-mobile-page'
import { TimezoneSelect } from './timezone-select'
import type { TimezoneSelectOption } from './timezone-select-options'
import {
  getProfileInitial,
  useAvatarEditor,
  usePasswordEditor,
  useProfileQuery,
  useProfileWorkspace,
  type ProfileTab,
} from './use-profile-page'

const automaticTimezoneValue = '__automatic__'

function CloseIcon() {
  return (
    <svg aria-hidden className="size-5" fill="none" viewBox="0 0 20 20">
      <path
        d="m5 5 10 10M15 5 5 15"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="1.8"
      />
    </svg>
  )
}

function CameraIcon() {
  return (
    <svg aria-hidden className="size-4" fill="none" viewBox="0 0 16 16">
      <path
        d="M3 5.2h2l.8-1.4h4.4l.8 1.4h2A1.2 1.2 0 0 1 14.2 6.4v6A1.2 1.2 0 0 1 13 13.6H3a1.2 1.2 0 0 1-1.2-1.2v-6A1.2 1.2 0 0 1 3 5.2Z"
        stroke="currentColor"
        strokeLinejoin="round"
        strokeWidth="1.2"
      />
      <path
        d="M8 11a2.1 2.1 0 1 0 0-4.2A2.1 2.1 0 0 0 8 11Z"
        stroke="currentColor"
        strokeWidth="1.2"
      />
    </svg>
  )
}

function PencilIcon() {
  return (
    <svg aria-hidden className="size-3.5" fill="none" viewBox="0 0 16 16">
      <path
        d="M9.7 3.1 12.9 6.3M2.7 10.9 10.9 2.7a1.7 1.7 0 0 1 2.4 2.4l-8.2 8.2-3.1.7.7-3.1Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.35"
      />
    </svg>
  )
}

function ChevronRightIcon() {
  return (
    <svg aria-hidden className="size-4" fill="none" viewBox="0 0 16 16">
      <path
        d="m6 4 4 4-4 4"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.6"
      />
    </svg>
  )
}

export function UserProfilePage() {
  const isMobile = useMediaQuery('(max-width: 767px)')

  if (isMobile) return <ProfileMobilePage />

  return <ProfileDesktopPage />
}

function ProfileDesktopPage() {
  const { t } = useTranslation('userProfile')
  const navigate = useNavigate()
  const profileQuery = useProfileQuery()

  return (
    <UserShellPage activePath="/profile">
      <div className="flex h-full flex-col overflow-hidden bg-white">
        <PageHeader
          actions={
            <IconButton
              aria-label={t('actions.close')}
              className="size-9 text-[var(--color-text-muted)]"
              onClick={() => {
                void navigate({ to: '/apps' })
              }}
              size="sm"
            >
              <CloseIcon />
            </IconButton>
          }
          title={t('title')}
        />
        <div className="min-h-0 flex-1 overflow-y-auto px-5 pt-2 pb-8 lg:px-[30px]">
          {profileQuery.isError ? (
            <div className="flex min-h-[360px] items-center justify-center">
              <ErrorState
                description={t('loadErrorDescription')}
                onRetry={() => {
                  void profileQuery.refetch()
                }}
                title={t('loadErrorTitle')}
              />
            </div>
          ) : profileQuery.isLoading || !profileQuery.data ? (
            <ProfileSkeleton />
          ) : (
            <ProfileWorkspace
              key={`${profileQuery.data.id}:${profileQuery.data.name}`}
              profile={profileQuery.data}
            />
          )}
        </div>
      </div>
    </UserShellPage>
  )
}

function ProfileSkeleton() {
  return (
    <div className="mx-auto grid max-w-[800px] gap-4">
      <Skeleton className="h-11 rounded-lg" />
      <Skeleton className="h-[300px] rounded-xl" />
    </div>
  )
}

function ProfileWorkspace({ profile }: { profile: UserProfile }) {
  const {
    activeTab,
    avatarEditorOpen,
    closeNameEditor,
    deleteAvatarMutation,
    name,
    nameEditorOpen,
    nameError,
    openNameEditor,
    saveProfileMutation,
    saveTimezoneMutation,
    selectTab,
    setAvatarEditorOpen,
    setName,
    setNameError,
    submitName,
    t,
    uploadAvatarMutation,
  } = useProfileWorkspace(profile)

  return (
    <div className="mx-auto grid max-w-[800px] gap-4">
      <ProfileTabs activeTab={activeTab} onSelectTab={selectTab} />

      <div className="grid gap-4">
        <section className="rounded-xl border border-[#ededee] bg-white px-5 py-5 sm:px-6">
          <div className="flex min-w-0 items-center gap-4">
            <AvatarButton
              avatar={profile.avatar ?? null}
              initial={getProfileInitial(profile)}
              label={t('avatarEditor.edit')}
              onClick={() => setAvatarEditorOpen(true)}
            />

            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-1.5">
                <h2 className="truncate text-base font-bold text-[var(--color-text)]">
                  {profile.name || t('fallbackName')}
                </h2>
                <IconButton
                  aria-label={t('nameEditor.edit')}
                  className="size-7 shrink-0 text-[#6b7280] hover:text-[var(--color-text)]"
                  onClick={openNameEditor}
                  size="sm"
                >
                  <PencilIcon />
                </IconButton>
              </div>
              <p className="mt-1 truncate text-xs text-[#6b7280]">{profile.email}</p>
              {profile.email_managed ? (
                <p className="mt-1 text-xs text-[var(--color-text-muted)]">{t('emailManaged')}</p>
              ) : null}
            </div>
          </div>

          <AvatarEditorModal
            avatar={profile.avatar ?? null}
            closeLabel={t('avatarEditor.close')}
            cropFailedMessage={t('avatarEditor.cropFailed')}
            initial={getProfileInitial(profile)}
            isRemoving={deleteAvatarMutation.isPending}
            isUploading={uploadAvatarMutation.isPending}
            onClose={() => setAvatarEditorOpen(false)}
            onRemove={async () => {
              await deleteAvatarMutation.mutateAsync()
              setAvatarEditorOpen(false)
            }}
            onUpload={async (file) => {
              await uploadAvatarMutation.mutateAsync(file)
              setAvatarEditorOpen(false)
            }}
            open={avatarEditorOpen}
            title={t('avatarEditor.title')}
          />
          <NameEditorModal
            closeLabel={t('nameEditor.close')}
            error={nameError}
            isSaving={saveProfileMutation.isPending}
            name={name}
            onChange={(nextName) => {
              setName(nextName)
              if (nameError) {
                setNameError('')
              }
            }}
            onClose={closeNameEditor}
            onSubmit={submitName}
            open={nameEditorOpen}
            title={t('nameEditor.title')}
          />
        </section>

        <TimezoneCard
          key={profile.timezone ?? automaticTimezoneValue}
          isSaving={saveTimezoneMutation.isPending}
          onSave={(timezone) => saveTimezoneMutation.mutateAsync({ timezone })}
          timezone={profile.timezone ?? null}
        />

        <PasswordCard profileEmail={profile.email} />
      </div>
    </div>
  )
}

function TimezoneCard({
  isSaving,
  onSave,
  timezone,
}: {
  isSaving: boolean
  onSave: (timezone: string | null) => Promise<UserProfile>
  timezone: string | null
}) {
  const { i18n, t } = useTranslation('userProfile')
  const deviceTimezone = useMemo(() => getDeviceTimezone(), [])
  const locale = i18n.resolvedLanguage ?? i18n.language
  const savedValue = timezone ?? automaticTimezoneValue
  const [selectedValue, setSelectedValue] = useState(savedValue)
  const timezoneOptions = useMemo<TimezoneSelectOption[]>(() => {
    const displayOptions = getTimezoneDisplayOptions(
      getSupportedTimezones([deviceTimezone, timezone]),
      locale,
    )
    const deviceOption = displayOptions.find((option) => option.timezone === deviceTimezone)

    return [
      {
        description: deviceOption
          ? t('timezone.automaticDescription', {
              location: deviceOption.location,
              timezone: deviceTimezone,
            })
          : t('timezone.automaticDescriptionUnavailable'),
        label: deviceOption
          ? t('timezone.automaticWithDevice', { timezone: deviceOption.summaryLabel })
          : t('timezone.automatic'),
        searchText: [t('timezone.automatic'), deviceOption?.searchText, deviceTimezone].join(' '),
        value: automaticTimezoneValue,
      },
      ...displayOptions.map((option) => ({
        description: option.timezone,
        label: option.label,
        searchText: option.searchText,
        value: option.timezone,
      })),
    ]
  }, [deviceTimezone, locale, t, timezone])

  const selectTimezone = async (value: string) => {
    setSelectedValue(value)

    try {
      await onSave(value === automaticTimezoneValue ? null : value)
    } catch {
      setSelectedValue(savedValue)
    }
  }

  return (
    <section className="grid gap-2">
      <h3 className="px-1 text-sm font-semibold text-[var(--color-text)]">
        {t('timezone.sectionTitle')}
      </h3>
      <div className="overflow-hidden rounded-xl border border-[#ededee] bg-white px-4 sm:px-5">
        <div className="flex min-h-[76px] flex-col justify-center gap-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
          <div className="min-w-0">
            <div className="text-sm font-medium text-[var(--color-text)]">
              {t('timezone.title')}
            </div>
            <p className="mt-0.5 text-xs leading-5 text-[var(--color-text-muted)]">
              {t('timezone.description')}
            </p>
          </div>
          <TimezoneSelect
            ariaLabel={t('timezone.title')}
            className="w-full shrink-0 sm:w-[280px]"
            disabled={isSaving}
            emptyText={t('timezone.searchEmpty')}
            onValueChange={(value) => {
              void selectTimezone(value)
            }}
            options={timezoneOptions}
            searchLabel={t('timezone.searchLabel')}
            searchPlaceholder={t('timezone.searchPlaceholder')}
            value={selectedValue}
          />
        </div>
      </div>
    </section>
  )
}

function AvatarButton({
  avatar,
  initial,
  label,
  onClick,
}: {
  avatar: string | null
  initial: string
  label: string
  onClick: () => void
}) {
  return (
    <button
      aria-label={label}
      className="group/avatar relative size-[54px] shrink-0 overflow-hidden rounded-full bg-[var(--color-primary)] text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]"
      onClick={onClick}
      type="button"
    >
      {avatar ? (
        <img alt="" className="h-full w-full object-cover" decoding="async" src={avatar} />
      ) : (
        <span className="flex h-full w-full items-center justify-center text-[30px] leading-none">
          {initial}
        </span>
      )}
      <span className="absolute inset-0 flex items-center justify-center bg-[rgba(17,24,39,0.5)] opacity-0 transition-opacity group-hover/avatar:opacity-100 group-focus-visible/avatar:opacity-100">
        <CameraIcon />
      </span>
    </button>
  )
}

function ProfileTabs({
  activeTab,
  onSelectTab,
}: {
  activeTab: ProfileTab
  onSelectTab: (tab: ProfileTab) => void
}) {
  const { t } = useTranslation('userProfile')
  const tabs: ProfileTab[] = ['basic']

  return (
    <div
      aria-label={t('tabs.label')}
      className="flex h-[60px] items-start gap-6 border-b border-[#ededee] bg-white pt-[17px]"
      role="tablist"
    >
      {tabs.map((tab) => (
        <button
          aria-selected={activeTab === tab}
          className={cn(
            'relative h-7 cursor-pointer px-0 text-sm font-normal transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
            activeTab === tab
              ? 'text-[var(--color-primary)]'
              : 'text-[var(--color-text)] hover:text-[var(--color-primary)]',
          )}
          key={tab}
          onClick={() => onSelectTab(tab)}
          role="tab"
          type="button"
        >
          {t(`tabs.${tab}`)}
          {activeTab === tab ? (
            <span className="absolute right-0 bottom-0 left-0 h-0.5 bg-[var(--color-primary)]" />
          ) : null}
        </button>
      ))}
    </div>
  )
}

function NameEditorModal({
  closeLabel,
  error,
  isSaving,
  name,
  onChange,
  onClose,
  onSubmit,
  open,
  title,
}: {
  closeLabel: string
  error: string
  isSaving: boolean
  name: string
  onChange: (name: string) => void
  onClose: () => void
  onSubmit: (event: SyntheticEvent<HTMLFormElement>) => void
  open: boolean
  title: string
}) {
  const { t } = useTranslation('userProfile')

  return (
    <Modal
      className="max-w-[430px]"
      closeLabel={closeLabel}
      footer={
        <>
          <Button disabled={isSaving} onClick={onClose} variant="secondary">
            {t('nameEditor.cancel')}
          </Button>
          <Button form="profile-name-form" isLoading={isSaving} type="submit">
            {t('nameEditor.submit')}
          </Button>
        </>
      }
      onClose={onClose}
      open={open}
      title={title}
    >
      <form className="grid gap-4" id="profile-name-form" onSubmit={onSubmit}>
        <FormField
          error={error}
          htmlFor="profile-display-name"
          label={t('fields.displayName')}
          required
        >
          <Input
            id="profile-display-name"
            invalid={Boolean(error)}
            onChange={(event) => onChange(event.target.value)}
            placeholder={t('nameEditor.placeholder')}
            value={name}
          />
        </FormField>
      </form>
    </Modal>
  )
}

function AvatarEditorModal({
  avatar,
  closeLabel,
  cropFailedMessage,
  initial,
  isRemoving,
  isUploading,
  onClose,
  onRemove,
  onUpload,
  open,
  title,
}: {
  avatar: string | null
  closeLabel: string
  cropFailedMessage: string
  initial: string
  isRemoving: boolean
  isUploading: boolean
  onClose: () => void
  onRemove: () => Promise<void>
  onUpload: (file: File) => Promise<void>
  open: boolean
  title: string
}) {
  const {
    closeAndReset,
    cropError,
    draftUrl,
    fileInputRef,
    handleFileChange,
    isBusy,
    previewAvatar,
    removeAvatar,
    setZoom,
    submitAvatar,
    t,
    zoom,
  } = useAvatarEditor({
    avatar,
    cropFailedMessage,
    isRemoving,
    isUploading,
    onClose,
    onRemove,
    onUpload,
  })

  return (
    <Modal
      className="max-w-[430px]"
      closeLabel={closeLabel}
      footer={
        <>
          {avatar ? (
            <Button disabled={isBusy} isLoading={isRemoving} onClick={removeAvatar} variant="ghost">
              {t('avatarEditor.remove')}
            </Button>
          ) : null}
          <Button disabled={isBusy} onClick={closeAndReset} variant="secondary">
            {t('avatarEditor.cancel')}
          </Button>
          <Button isLoading={isUploading} onClick={submitAvatar}>
            {draftUrl ? t('avatarEditor.save') : t('avatarEditor.choose')}
          </Button>
        </>
      }
      onClose={closeAndReset}
      open={open}
      title={title}
    >
      <div className="grid gap-5">
        <div className="mx-auto grid justify-items-center gap-3">
          <div className="relative size-40 overflow-hidden rounded-full bg-[var(--color-primary)] text-white">
            {previewAvatar ? (
              <img
                alt=""
                className="h-full w-full object-cover transition-transform duration-200"
                decoding="async"
                src={previewAvatar}
                style={{ transform: draftUrl ? `scale(${zoom.toFixed(2)})` : undefined }}
              />
            ) : (
              <span className="flex h-full w-full items-center justify-center text-[64px] leading-none">
                {initial}
              </span>
            )}
            <span className="pointer-events-none absolute inset-0 rounded-full ring-1 ring-[rgba(26,28,25,0.08)] ring-inset" />
          </div>

          <input
            accept="image/png,image/jpeg,image/webp,image/gif"
            className="hidden"
            onChange={handleFileChange}
            ref={fileInputRef}
            type="file"
          />
          {draftUrl ? (
            <Button
              disabled={isBusy}
              onClick={() => fileInputRef.current?.click()}
              size="sm"
              variant="secondary"
            >
              {t('avatarEditor.chooseAnother')}
            </Button>
          ) : null}
        </div>

        {draftUrl ? (
          <label className="grid gap-2 text-sm font-semibold text-[var(--color-text)]">
            {t('avatarEditor.zoom')}
            <input
              className="h-2 w-full cursor-pointer accent-[var(--color-primary)]"
              max="2"
              min="1"
              onChange={(event) => setZoom(Number(event.target.value))}
              step="0.01"
              type="range"
              value={zoom}
            />
          </label>
        ) : null}

        {cropError ? <p className="text-sm text-[var(--color-danger)]">{cropError}</p> : null}
        <p className="text-center text-xs text-[var(--color-text-muted)]">
          {t('avatarEditor.fileHint')}
        </p>
      </div>
    </Modal>
  )
}

function PasswordCard({ profileEmail }: { profileEmail: string }) {
  const { t } = useTranslation('userProfile')
  const [passwordEditorOpen, setPasswordEditorOpen] = useState(false)

  return (
    <section className="overflow-hidden rounded-xl border border-[#ededee] bg-white">
      <h3 className="px-4 py-3 text-sm font-bold text-[var(--color-text)] sm:px-5">
        {t('password.title')}
      </h3>

      <button
        className="flex min-h-12 w-full items-center justify-between border-t border-[rgba(26,28,25,0.06)] px-4 text-left text-sm text-[var(--color-text)] transition-colors hover:bg-[#f8f8f8] focus-visible:outline focus-visible:outline-2 focus-visible:outline-inset focus-visible:outline-[var(--color-focus)] sm:px-5"
        onClick={() => setPasswordEditorOpen(true)}
        type="button"
      >
        <span>{t('password.reset')}</span>
        <span className="text-[#6b7280]">
          <ChevronRightIcon />
        </span>
      </button>

      <PasswordEditorModal
        closeLabel={t('password.close')}
        onClose={() => setPasswordEditorOpen(false)}
        open={passwordEditorOpen}
        profileEmail={profileEmail}
        title={t('password.modalTitle')}
      />
    </section>
  )
}

function PasswordEditorModal({
  closeLabel,
  onClose,
  open,
  profileEmail,
  title,
}: {
  closeLabel: string
  onClose: () => void
  open: boolean
  profileEmail: string
  title: string
}) {
  const {
    closeAndReset,
    passwordErrors,
    passwordForm,
    passwordMutation,
    submitPassword,
    t,
    updatePasswordField,
  } = usePasswordEditor({ onClose, profileEmail })

  return (
    <Modal
      className="max-w-[430px]"
      closeLabel={closeLabel}
      footer={
        <>
          <Button disabled={passwordMutation.isPending} onClick={closeAndReset} variant="secondary">
            {t('password.cancel')}
          </Button>
          <Button form="profile-password-form" isLoading={passwordMutation.isPending} type="submit">
            {t('password.submit')}
          </Button>
        </>
      }
      onClose={closeAndReset}
      open={open}
      title={title}
    >
      <div className="grid gap-4">
        <InlineNotice className="py-2.5" tone="warning">
          {t('password.reloginNotice')}
        </InlineNotice>

        <form className="grid gap-4" id="profile-password-form" onSubmit={submitPassword}>
          <FormField
            error={passwordErrors.currentPassword}
            htmlFor="profile-currentPassword"
            label={t('fields.currentPassword')}
            required
          >
            <PasswordInput
              autoComplete="current-password"
              hideLabel={t('password.hidePassword')}
              id="profile-currentPassword"
              invalid={Boolean(passwordErrors.currentPassword)}
              onChange={(event) => updatePasswordField('currentPassword', event.target.value)}
              showLabel={t('password.showPassword')}
              value={passwordForm.currentPassword}
            />
          </FormField>
          <FormField
            error={passwordErrors.newPassword}
            htmlFor="profile-newPassword"
            label={t('fields.newPassword')}
            required
          >
            <PasswordInput
              autoComplete="new-password"
              hideLabel={t('password.hidePassword')}
              id="profile-newPassword"
              invalid={Boolean(passwordErrors.newPassword)}
              onChange={(event) => updatePasswordField('newPassword', event.target.value)}
              showLabel={t('password.showPassword')}
              value={passwordForm.newPassword}
            />
          </FormField>
          <FormField
            error={passwordErrors.confirmPassword}
            htmlFor="profile-confirmPassword"
            label={t('fields.confirmPassword')}
            required
          >
            <PasswordInput
              autoComplete="new-password"
              hideLabel={t('password.hidePassword')}
              id="profile-confirmPassword"
              invalid={Boolean(passwordErrors.confirmPassword)}
              onChange={(event) => updatePasswordField('confirmPassword', event.target.value)}
              showLabel={t('password.showPassword')}
              value={passwordForm.confirmPassword}
            />
          </FormField>
        </form>
      </div>
    </Modal>
  )
}
